/**
 * Search results, notifications and portfolio activity pages.
 */
import { Badge, Button, Card, ListGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router";
import { Timeline } from "../components/Timeline.tsx";
import { DateRangePicker } from "../components/ui/DateRangePicker.tsx";
import { FilterBar, FilterSelect } from "../components/ui/Filters.tsx";
import { EmptyState, PageHeader, QueryState, Section } from "../components/ui/kit.tsx";
import { api } from "../lib/api.ts";
import { useFormat } from "../lib/format.ts";
import { toOptions, useLookups } from "../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../lib/query.ts";
import { useDateRange, useUrlState } from "../lib/url-state.ts";

interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  link: string;
}

export function SearchPage() {
  const { get } = useUrlState();
  const q = get("q");
  const results = useApi<SearchResult[]>(q.length >= 2 ? "/api/search" : null, { q, full: true });
  const groups = new Map<string, SearchResult[]>();
  for (const r of results.data ?? []) groups.set(r.type, [...(groups.get(r.type) ?? []), r]);
  return (
    <>
      <PageHeader title={`Search results for “${q}”`} />
      <QueryState query={{ ...results, data: results.data ?? (q.length < 2 ? [] : undefined) }}>
        {() =>
          groups.size === 0 ? (
            <EmptyState icon="search" title="No results">
              Try an address, postcode, tenant name, reference or description.
            </EmptyState>
          ) : (
            [...groups.entries()].map(([type, items]) => (
              <Section key={type} title={<span className="text-capitalize">{type}s</span>} flush>
                <ListGroup variant="flush">
                  {items.map((r) => (
                    <ListGroup.Item key={r.id} action as={Link} to={r.link}>
                      <div className="fw-semibold">{r.title}</div>
                      {r.subtitle && <div className="small text-muted">{r.subtitle}</div>}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Section>
            ))
          )
        }
      </QueryState>
    </>
  );
}

interface NotificationList {
  items: Array<{
    id: string;
    type: string;
    title: string;
    body: string;
    link: string;
    readAt: string | null;
    createdAt: string;
    dueDate: string | null;
  }>;
  unread: number;
}

export function NotificationsPage() {
  const q = useApi<NotificationList>("/api/notifications", { limit: 200 });
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Reminders for tasks, compliance, rent, tenancies, finance and tax deadlines"
        actions={
          <Button
            variant="outline-secondary"
            onClick={async () => {
              await api.post("/api/notifications/read-all");
              await invalidate();
            }}
          >
            Mark all read
          </Button>
        }
      />
      <Card>
        <QueryState query={q}>
          {(d) =>
            d.items.length === 0 ? (
              <EmptyState icon="bell" title="You're all caught up">
                Reminders appear here as due dates approach. Set preferences in Settings → Notifications.
              </EmptyState>
            ) : (
              <ListGroup variant="flush">
                {d.items.map((n) => (
                  <ListGroup.Item
                    key={n.id}
                    action
                    className={n.readAt ? "" : "notification-item unread"}
                    onClick={async () => {
                      await api.post(`/api/notifications/${n.id}/read`);
                      await invalidate();
                      if (n.link) navigate(n.link);
                    }}
                  >
                    <div className="d-flex justify-content-between gap-2">
                      <span className="fw-semibold">{n.title}</span>
                      <Badge bg="light" text="dark" className="text-capitalize">
                        {n.type}
                      </Badge>
                    </div>
                    {n.body && <div className="small text-muted">{n.body}</div>}
                    <div className="small text-muted">{f.dateTime(n.createdAt)}</div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )
          }
        </QueryState>
      </Card>
    </>
  );
}

export function TimelinePage() {
  const range = useDateRange("last_12_months");
  const { get } = useUrlState();
  const lookups = useLookups();
  return (
    <>
      <PageHeader title="Activity" subtitle="Everything that has happened across your portfolio" />
      <Card>
        <FilterBar>
          <DateRangePicker range={range} />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
        </FilterBar>
        <Card.Body>
          <Timeline filters={{ from: range.from, to: range.to, propertyId: get("propertyId") }} limit={50} />
        </Card.Body>
      </Card>
    </>
  );
}
